import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type {
  BillingAccountSummaryView,
  BillingAccountsPage,
  SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { TaskSection } from './TaskSection';
import { Link } from 'react-router-dom';
import { apiRequest } from './api';
import { formatMoney } from './billing';
import { AnnualFeesPanel } from './AnnualFeesPanel';
import { BillingAccountView } from './BillingAccountView';
import { BillingFeesPanel } from './BillingFeesPanel';
import { ImportsPanel } from './ImportsPanel';
import { BillingPeriodsPanel } from './BillingPeriodsPanel';
import { BillingReportsPanel } from './BillingReportsPanel';
import { errorKeyOf, type ErrorKey } from './BillingForms';

/** Same density as the athletes table; on a phone a row keeps the name, balance and Open. */
const wideColumn = { display: { xs: 'none', md: 'table-cell' } } as const;
const mediumColumn = { display: { xs: 'none', sm: 'table-cell' } } as const;
const compactTable = {
  '& .MuiTableCell-root': { py: 0.25, lineHeight: 1.3 },
  '& .MuiTableCell-head': { py: 0.75 },
  '& .MuiButton-root': { py: 0, minHeight: 0 },
  '& .MuiChip-root': { height: 20 },
} as const;

export interface BillingPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
  /** Opens this account straight away (a deep link). */
  initialAccountId?: string | undefined;
}

/**
 * The finance view of the dues ledger: every billing account with its own position, never summed
 * across accounts, and the selected account's charges, payments and postings.
 */
export function BillingPanel({
  locale,
  organizationId,
  csrfToken,
  initialAccountId,
}: BillingPanelProps): React.JSX.Element {
  const base = `/api/v1/organizations/${organizationId}/billing/accounts`;
  const [accounts, setAccounts] = useState<BillingAccountSummaryView[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [label, setLabel] = useState('');

  const load = useCallback(
    async (cursor: string | null) => {
      try {
        const query = cursor === null ? '' : `?cursor=${encodeURIComponent(cursor)}`;
        const page = await apiRequest<BillingAccountsPage>(`${base}${query}`);
        setAccounts((current) =>
          cursor === null ? page.items : [...(current ?? []), ...page.items],
        );
        setNextCursor(page.nextCursor);
      } catch (error) {
        setErrorKey(errorKeyOf(error));
      }
    },
    [base],
  );

  useEffect(() => {
    if (initialAccountId === undefined) void load(null);
  }, [initialAccountId, load]);

  async function create(): Promise<void> {
    setErrorKey(null);
    try {
      await apiRequest(base, { method: 'POST', body: { label: label.trim() }, csrfToken });
      setLabel('');
      await load(null);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  if (initialAccountId !== undefined) {
    return (
      <BillingAccountView
        locale={locale}
        organizationId={organizationId}
        accountId={initialAccountId}
        csrfToken={csrfToken}
      />
    );
  }

  return (
    <Box component="section" aria-label={translate(locale, 'nav.billing')} sx={{ mb: 3 }}>
      <TaskSection
        locale={locale}
        titleKey="billing.accounts.title"
        descriptionKey="task.billingAccounts.description"
        defaultOpen
      >
        {errorKey !== null && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {translate(locale, errorKey)}
          </Alert>
        )}
        <Stack
          component="form"
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mb: 2 }}
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <TextField
            required
            size="small"
            label={translate(locale, 'billing.accounts.create.label')}
            value={label}
            onChange={(event) => {
              setLabel(event.target.value);
            }}
            slotProps={{ htmlInput: { maxLength: 120 } }}
            sx={{ minWidth: 260 }}
          />
          <Button type="submit" variant="contained" disabled={label.trim() === ''}>
            {translate(locale, 'billing.accounts.create.submit')}
          </Button>
        </Stack>
        {accounts === null && errorKey === null && (
          <Typography>{translate(locale, 'billing.accounts.loading')}</Typography>
        )}
        {accounts?.length === 0 && (
          <Typography>{translate(locale, 'billing.accounts.empty')}</Typography>
        )}
        {accounts !== null && accounts.length > 0 && (
          <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
            <Table
              size="small"
              aria-label={translate(locale, 'billing.accounts.title')}
              sx={compactTable}
            >
              <TableHead>
                <TableRow>
                  <TableCell>{translate(locale, 'billing.accounts.table.account')}</TableCell>
                  <TableCell align="right">
                    {translate(locale, 'billing.position.balance')}
                  </TableCell>
                  <TableCell align="right" sx={mediumColumn}>
                    {translate(locale, 'billing.position.overdue')}
                  </TableCell>
                  <TableCell align="right" sx={wideColumn}>
                    {translate(locale, 'billing.position.upcoming')}
                  </TableCell>
                  <TableCell align="right" sx={wideColumn}>
                    {translate(locale, 'billing.position.credit')}
                  </TableCell>
                  <TableCell align="right">
                    {translate(locale, 'billing.accounts.table.actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.id} hover>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                      {account.label}{' '}
                      {account.status === 'closed' && (
                        <Chip size="small" label={translate(locale, 'billing.accounts.closed')} />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {formatMoney(account.position.balanceMinor, account.currency, locale)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...mediumColumn, whiteSpace: 'nowrap' }}>
                      {formatMoney(account.position.overdueMinor, account.currency, locale)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...wideColumn, whiteSpace: 'nowrap' }}>
                      {formatMoney(account.position.upcomingMinor, account.currency, locale)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...wideColumn, whiteSpace: 'nowrap' }}>
                      {formatMoney(
                        account.position.unallocatedCreditMinor,
                        account.currency,
                        locale,
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        component={Link}
                        to={`/billing/accounts/${account.id}`}
                        aria-label={`${translate(locale, 'billing.accounts.open')}: ${account.label}`}
                      >
                        {translate(locale, 'billing.accounts.open')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {nextCursor !== null && (
          <Button
            sx={{ mt: 2 }}
            onClick={() => {
              void load(nextCursor);
            }}
          >
            {translate(locale, 'billing.accounts.loadMore')}
          </Button>
        )}
      </TaskSection>
      <TaskSection
        locale={locale}
        titleKey="billing.annualFees.title"
        descriptionKey="billing.annualFees.description"
      >
        <AnnualFeesPanel locale={locale} organizationId={organizationId} csrfToken={csrfToken} />
      </TaskSection>
      <TaskSection locale={locale} titleKey="task.fees" descriptionKey="task.fees.description">
        <BillingFeesPanel locale={locale} organizationId={organizationId} csrfToken={csrfToken} />
      </TaskSection>
      <TaskSection
        locale={locale}
        titleKey="billing.periods.title"
        descriptionKey="task.periods.description"
      >
        <BillingPeriodsPanel
          locale={locale}
          organizationId={organizationId}
          csrfToken={csrfToken}
        />
      </TaskSection>
      <TaskSection
        locale={locale}
        titleKey="task.financeImports"
        descriptionKey="task.imports.description"
      >
        <ImportsPanel
          locale={locale}
          organizationId={organizationId}
          csrfToken={csrfToken}
          scope="finance"
        />
      </TaskSection>
      <TaskSection
        locale={locale}
        titleKey="billing.reports.title"
        descriptionKey="task.reports.description"
      >
        <BillingReportsPanel
          locale={locale}
          organizationId={organizationId}
          accounts={accounts ?? []}
        />
      </TaskSection>
    </Box>
  );
}
